import React, { useCallback, useContext, useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import mixpanel from 'mixpanel-browser';
import { useTranslation } from 'react-i18next';
import { AppContext } from './AppContext';
import { sanitizeReferrerAndReferringDomain } from './generalUtils';

interface MixpanelWrapper {
  sendMixpanelEvent: typeof mixpanel.track;
  sendMixpanelError: (p: {
    email: string;
    errorKey: string;
    screen: string;
    axiosError?: AxiosError<any>;
  }) => void;
}

const defaultMixpanel = {
  sendMixpanelEvent: () => {},
  sendMixpanelError: () => {},
};

export const SCREENS = {
  EMAIL_FORM: 'email form',
  PASSWORD_FORM: 'password form',
  FORGOT_PASSWORD: 'forgot password',
  RESET_PASSWORD: 'reset password',
};

export const MixpanelContext = React.createContext<MixpanelWrapper>(defaultMixpanel);

const initMixpanel = async (
  token: string,
  setIsMixpanelLoaded: React.Dispatch<React.SetStateAction<boolean>>
) => {
  if (token) {
    mixpanel.init(token, {
      loaded: () => setIsMixpanelLoaded(true),
      save_referrer: false
    });
    const {referrer, referringDomain} = sanitizeReferrerAndReferringDomain(document.referrer);
    mixpanel.register({
      $initial_referrer: referrer,
      $referrer: referrer,
      $initial_referring_domain: referringDomain,
      $referring_domain: referringDomain,
    });
  }
};

export const MixpanelProvider: React.FC = ({ children }) => {
  const [isMixpanelLoaded, setIsMixpanelLoaded] = useState(false);
  const { customer, envData } = useContext(AppContext);
  const { t } = useTranslation();

  React.useEffect(() => {
    initMixpanel(envData?.MIXPANEL_TOKEN, setIsMixpanelLoaded);
  }, [envData?.MIXPANEL_TOKEN]);

  const sendMixpanelEvent = useCallback<MixpanelWrapper['sendMixpanelEvent']>(
    (eventName, properties, callback) => {
      // TODO: uncomment after fixing the mixpanel user merging problem
      // if (isMixpanelLoaded) {
      // mixpanel.track(
      //   eventName,
      //   {
      //     referrerDomain: paramsObject.referrerURL?.host,
      //     Customer: paramsObject.customer,
      //     url: window.location.href,
      //     ...properties
      //   },
      //   callback
      // );
      // } else
      if (typeof callback === 'function') {
        // If mixpanel wasnt initialized, execute the mixpanel track calllback with an error code (0)
        callback(0);
      }
    },
    [envData, customer, isMixpanelLoaded]
  );

  const sendMixpanelError = useCallback<MixpanelWrapper['sendMixpanelError']>(
    ({ email, errorKey, screen, axiosError }) => {
      const error = axiosError?.response?.data?.error;
      const mixpanelProperties = {
        email,
        screen,
        errorDescreption: error?.description,
        errorShortDescription: error?.short_description,
        errorCategory: error?.category,
        errorSubCategory: error?.sub_category,
        errorEventType: error?.event_type ?? (axiosError ? 'unknown' : 'user_error'),
        errorStatusCode: axiosError?.response?.status,
        errorTitle: t(errorKey),
        errorKey,
        stackTrace: error?.stack ?? error?.stack_trace,
        // eslint-disable-next-line no-nested-ternary
        errorOrigin: error?.fromMithra ? 'mithra' : axiosError ? 'unknown' : 'login-service',
        message: error?.message,
        parameters: error?.parameters,
        data: error?.data,
        raw: error,
        isErrorModal: false, // errors in login service are never showd in error modal
        team: 'core',
        moduleName: 'login',
      };
      mixpanelProperties.errorEventType !== 'user_error' &&
        !error?.fromMithra &&
        Sentry.captureException(axiosError);
      sendMixpanelEvent('Error', mixpanelProperties);
    },
    [sendMixpanelEvent, t]
  );

  const mixpanelWrapper = useMemo(
    () => ({ sendMixpanelEvent, sendMixpanelError }),
    [sendMixpanelEvent, sendMixpanelError]
  );

  return <MixpanelContext.Provider value={mixpanelWrapper}>{children}</MixpanelContext.Provider>;
};
